import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, MapPin, Clock, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import CookieBanner from "@/components/CookieBanner";

const WHATSAPP_URL = "https://wa.me/34672972514?text=Hola%2C%20he%20leido%20tu%20articulo%20sobre%20perder%20dinero%20y%20me%20gustaria%20revisar%20mi%20caso";
const LOGO_URL = "/datyon-logo.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

export default function BlogArticle2() {
  useEffect(() => {
    document.title = "Cómo saber si tu empresa está perdiendo dinero | Datyon";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Tu empresa puede estar perdiendo dinero sin que lo sepas. Descubre las señales, los problemas más comunes y cómo el análisis de datos puede ayudarte a mejorar resultados.");
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://datyon.com/blog/como-saber-si-tu-empresa-pierde-dinero");
    }
    window.scrollTo(0, 0);
    return () => {
      document.title = "Análisis de Datos en Murcia para Empresas | Datyon";
      if (metaDesc) metaDesc.setAttribute("content", "Transforma los datos de tu empresa en decisiones rentables.");
      if (canonical) canonical.setAttribute("href", "https://datyon.com/");
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-x-hidden" data-testid="blog-article2-page">
      <div className="noise-overlay" />

      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="article2-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center pl-4">
              <img src={LOGO_URL} alt="DATYON" className="h-20 w-auto" style={{ maxHeight: '80px' }} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Inicio</Link>
              <Link to="/servicios" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Servicios</Link>
              <Link to="/sobre-mi" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Sobre mí</Link>
              <Link to="/blog" className="nav-link text-indigo-400 hover:text-white text-sm font-medium transition-colors">Blog</Link>
              <Link to="/contacto" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Contacto</Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        <article className="relative section-padding pt-32 overflow-hidden" data-testid="article2-content">
          <div className="spotlight-1 -top-40 -left-40" />
          <div className="max-w-3xl mx-auto relative z-10">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="mb-8">
                <Link to="/blog" className="inline-flex items-center text-slate-400 hover:text-indigo-400 text-sm transition-colors mb-6">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al blog
                </Link>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-xs font-medium text-indigo-400 bg-indigo-400/10 px-3 py-1 rounded-full">Estrategia de datos</span>
                  <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> 5 min de lectura</span>
                </div>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-8">
                Cómo saber si tu empresa está perdiendo dinero (y no te estás dando cuenta)
              </motion.h1>

              <motion.div variants={fadeInUp} className="prose-custom text-slate-400 text-base leading-relaxed space-y-6">
                <p className="text-lg text-slate-300">
                  Tu empresa puede estar perdiendo dinero… y probablemente no lo sabes.
                </p>

                <p>
                  No es una exageración.
                </p>

                <p>
                  Es algo que ocurre en muchas empresas, incluso en aquellas que aparentemente funcionan bien.
                </p>

                <p>
                  No se trata de que no vendan. No se trata de que no tengan clientes. Ni siquiera de que no tengan datos.
                </p>

                <p>
                  El problema es otro: están tomando decisiones sin entender realmente qué está pasando en su negocio.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">El problema no es la falta de datos</h2>

                <p>
                  Hoy en día, cualquier empresa tiene información de sobra: ventas, clientes, costes, campañas, operaciones.
                </p>

                <p>
                  El problema no es la cantidad de datos. Es que no se están utilizando bien.
                </p>

                <p>
                  Muchas empresas acumulan información, pero no la convierten en decisiones útiles. Y ahí es donde empieza la pérdida.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">"Nos va bien" no significa que lo estés haciendo bien</h2>

                <p>
                  Es muy común escuchar: "Nos va bien."
                </p>

                <p>
                  Y puede ser cierto. Hay facturación. Hay clientes. El negocio funciona.
                </p>

                <p>
                  Pero eso no significa que esté optimizado.
                </p>

                <p>
                  Muchas veces, las empresas están ganando dinero <em>a pesar</em> de sus decisiones, no gracias a ellas.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Dónde se pierde dinero en una empresa</h2>

                <p>
                  El problema no suele ser visible a simple vista. Pero aparece en decisiones mejorables, inversiones mal optimizadas, procesos ineficientes y oportunidades desaprovechadas.
                </p>

                <p>
                  Y todo impacta directamente en la rentabilidad. No es un problema de facturación. Es un problema de margen.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">La intuición tiene un límite</h2>

                <p>
                  Cuando el negocio crece, hay más datos, más decisiones y más complejidad. La intuición deja de ser suficiente.
                </p>

                <p>
                  Aquí es donde entra el <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">análisis de datos</Link>. No para sustituir la experiencia del empresario, sino para complementarla con información real.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Tener dashboards no es suficiente</h2>

                <p>
                  Muchas empresas tienen dashboards o informes, pero no saben responder preguntas básicas: qué es rentable, qué no lo es, dónde se pierde dinero.
                </p>

                <p>
                  El problema no es la herramienta. Es el análisis. Un dashboard sin un buen análisis detrás es solo un gráfico bonito. Si te interesa implementar <Link to="/business-intelligence-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">Business Intelligence de verdad</Link>, lo importante es que te ayude a tomar decisiones, no a decorar una pantalla.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Qué hace un analista de datos</h2>

                <p>
                  Un analista de datos identifica problemas, detecta oportunidades y transforma datos en decisiones claras y aplicables. No se trata de hacer informes largos ni de usar tecnología por usarla. Se trata de encontrar lo que no funciona y decirte qué hacer para mejorarlo.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">No necesitas más datos</h2>

                <p>
                  Necesitas usar mejor los que ya tienes. La mayoría de empresas ya tienen la información necesaria para mejorar sus resultados. Solo necesitan a alguien que la analice correctamente.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Conclusión</h2>

                <p>
                  Si no sabes dónde ganas dinero y dónde lo pierdes, no puedes mejorar. Pero esa información ya existe en tu empresa. Solo hay que analizarla correctamente.
                </p>

                <p>
                  Si quieres saber si tu empresa tiene margen de mejora, puedes solicitar un <Link to="/contacto" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">diagnóstico gratuito</Link>. En 7 días te digo dónde estás perdiendo dinero y qué puedes hacer para recuperarlo.
                </p>
              </motion.div>

              {/* CTA */}
              <motion.div variants={fadeInUp} className="mt-16 glass-card rounded-2xl p-8 md:p-10 text-center">
                <h2 className="text-2xl font-bold text-white mb-4">
                  ¿Quieres saber si tu empresa tiene margen de mejora?
                </h2>
                <p className="text-slate-400 mb-6">
                  Hablemos. Te digo dónde estás perdiendo dinero y qué puedes hacer para mejorarlo.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 rounded-full">
                    <Link to="/contacto">
                      Solicita tu diagnóstico gratuito
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 rounded-full transition-all hover:scale-105">
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      Escríbeme por WhatsApp
                    </a>
                  </Button>
                </div>
              </motion.div>

              {/* Related links */}
              <motion.div variants={fadeInUp} className="mt-12 text-sm text-slate-500">
                <p>Artículos relacionados: <Link to="/blog/analisis-datos-empresas-decisiones" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">Cómo mejorar decisiones empresariales con datos</Link></p>
                <p className="mt-2">Servicios: <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">Análisis de datos para empresas</Link> · <Link to="/business-intelligence-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">Business Intelligence en Murcia</Link> · <Link to="/consultor-datos-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">Consultor de datos en Murcia</Link></p>
              </motion.div>
            </motion.div>
          </div>
        </article>
      </main>

      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DATYON" className="h-8 w-auto" />
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span className="text-sm">Murcia, España</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">Inicio</Link>
              <Link to="/servicios" className="text-slate-500 hover:text-white text-sm transition-colors">Servicios</Link>
              <Link to="/blog" className="text-slate-500 hover:text-white text-sm transition-colors">Blog</Link>
              <Link to="/contacto" className="text-slate-500 hover:text-white text-sm transition-colors">Contacto</Link>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6 pt-4 border-t border-slate-800">
            <Link to="/aviso-legal" className="text-slate-500 hover:text-white text-sm transition-colors">Aviso Legal</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-privacidad" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Privacidad</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-cookies" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Cookies</Link>
          </div>
          <p className="text-slate-500 text-sm text-center md:text-left">
            &copy; {new Date().getFullYear()} DATYON - Antonio Oliver. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110 group"
        aria-label="Contactar por WhatsApp">
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>

      <CookieBanner />
    </div>
  );
}
